/**
 * Enums del modelo de datos (docs/spec/modelo-datos.md §2).
 * Fuente única para backend, SPA web, app de socios y agente de acceso.
 *
 * Convención: cada enum es un `as const` (array congelado) + su tipo union.
 * Nunca se agrega un valor acá sin que exista en el modelo de datos.
 */
const enumOf = (values) => values;
// ─────────────────────────────────────────────────────────────────────────────
// Estructura física (modelo-datos §2.1)
// ─────────────────────────────────────────────────────────────────────────────
/** `branches.kind` — «Central» es la sede virtual que recibe gastos del CUIT único (RN-CON-17). */
export const BRANCH_KINDS = enumOf(["operational", "central"]);
/** `accessPoints.type` — molinete GSD o QR estático de local (RN-ACC-01). */
export const ACCESS_POINT_TYPES = enumOf(["turnstile", "location_qr"]);
/** `accessPoints.state` — contingencia se activa/desactiva auditada (RN-ACC-24). */
export const ACCESS_POINT_STATES = enumOf(["online", "offline", "contingency"]);
// ─────────────────────────────────────────────────────────────────────────────
// Personas (modelo-datos §2.2)
// ─────────────────────────────────────────────────────────────────────────────
/** RN-ROL-01: los 6 roles del AF §11 más el Contador sólo-lectura (PV-82). */
export const USER_ROLES = enumOf([
    "owner",
    "admin",
    "intersede_coordinator",
    "branch_manager",
    "receptionist",
    "teacher",
    "accountant_readonly",
]);
/** RN-ROL-09: desactivar corta acceso al instante; jamás se borra. */
export const USER_STATUSES = enumOf(["active", "deactivated"]);
/** `staffRecords.type` — plantel del club (RN-PRO-01). */
export const STAFF_TYPES = enumOf([
    "sala_teacher",
    "class_teacher",
    "receptionist",
    "branch_manager",
    "admin",
    "intersede_coordinator",
]);
/** `staffRecords.hireMode` — dependencia o externo que factura (PV-53). */
export const HIRE_MODES = enumOf(["payroll", "external_billing"]);
export const STAFF_STATUSES = enumOf(["active", "inactive"]);
/** RN-SOC-01: el documento es la clave única de persona. */
export const DOC_TYPES = enumOf(["dni", "passport", "foreign_id"]);
/** RN-SOC-28: estados calculados a diario + terminales. */
export const MEMBER_STATUSES = enumOf([
    "active",
    "delinquent",
    "inactive",
    "terminated",
    "archived",
]);
/** RN-SOC-06: huella ilegible permanente → `dni_only`, sin re-pedidos. */
export const ACCESS_MODES = enumOf(["fingerprint", "dni_only"]);
/** RN-SOC-07: la ficha incompleta se persigue. */
export const INCOMPLETE_FLAGS = enumOf([
    "address",
    "apt",
    "emergency_contact",
    "email",
    "fingerprint",
]);
/** `members.migration.source` (RN-MIG-04/05). */
export const MIGRATION_SOURCES = enumOf(["crossfy", "controlgym", "drive"]);
/** RN-SOC-36: visitantes por convenio, nunca socios. */
export const PLATFORMS = enumOf(["sportclub", "wellhub"]);
/**
 * RN-PLA-36: un plan **complemento de plataforma** (Viva Infinity SportClub/Wellhub) se
 * vende sólo a un visitante activo de esa plataforma; `any` = de cualquiera de las dos.
 */
export const PLAN_PLATFORM_REQUIREMENTS = enumOf(["sportclub", "wellhub", "any"]);
export const VISITOR_STATUSES = enumOf(["active", "blocked", "converted"]);
export const FAMILY_GROUP_STATUSES = enumOf(["active", "dissolved"]);
/** `corporateAgreements.condition.kind` (RN-SOC-21). */
export const CORPORATE_CONDITION_KINDS = enumOf(["discount_pct", "company_billed"]);
export const CORPORATE_AGREEMENT_STATUSES = enumOf(["active", "expired", "terminated"]);
/** RN-CRM-03: convertido no retrocede. */
export const PROSPECT_STATUSES = enumOf([
    "new",
    "following",
    "trial_scheduled",
    "converted",
    "lost",
]);
// ─────────────────────────────────────────────────────────────────────────────
// Documentos legales y salud (modelo-datos §2.3)
// ─────────────────────────────────────────────────────────────────────────────
export const LEGAL_DOC_TYPES = enumOf([
    "rules",
    "image_consent",
    "minor_withdrawal",
    "debit_consent",
    "health_declaration_form",
    "trial_terms",
]);
/** `signedDocuments.person.kind`. */
export const SIGNER_KINDS = enumOf(["member", "visitor", "guardian", "third_party"]);
/** RN-SOC-24: app autenticada o tablet con firma manuscrita. */
export const SIGNATURE_MEDIUMS = enumOf(["app_authenticated", "kiosk_signature"]);
/** Personas con ficha propia que pueden tener apto/retiro (`{member|visitor}`). */
export const PERSON_KINDS = enumOf(["member", "visitor"]);
/** RN-SOC-11: la carga del socio queda pendiente hasta que recepción valida. */
export const MEDICAL_CERTIFICATE_STATUSES = enumOf(["pending_validation", "valid", "expired"]);
/** RN-SOC-42. */
export const WITHDRAWAL_MODES = enumOf(["emergency_service", "own_means", "family_third"]);
// ─────────────────────────────────────────────────────────────────────────────
// Planes, precios y cobranzas (modelo-datos §2.4)
// ─────────────────────────────────────────────────────────────────────────────
/** AF §4.1: catálogo único para las 3 sedes. */
export const PLAN_KINDS = enumOf([
    "monthly",
    "fortnight",
    "week",
    "quarterly",
    "semiannual",
    "annual",
    "reformer_only",
    "trial",
]);
/**
 * `plans.kind` dice la DURACIÓN; el ALCANCE lo dice `plans.accessProfile` (flyers octubre
 * 2026: Musculación anual/semestral, Solo Reformer con duración, AFI para no socios).
 * `reformer_only` queda en `PLAN_KINDS` sólo por los documentos existentes: se lee como
 * `monthly` + `disciplines_only` (ver `plan-access.ts`). No se crean planes nuevos con él.
 *
 * - `full`: pase libre (todas las clases, sujetas a sus topes).
 * - `disciplines_only`: sólo reserva clases de `allowedDisciplineIds` (RN-CLA-26).
 * - `strength_only`: no reserva NINGUNA clase; sólo entra a salas de musculación (RN-PLA-33).
 */
export const ACCESS_PROFILES = enumOf(["full", "disciplines_only", "strength_only"]);
/**
 * RN-ACC-11: alcances de local. `all` lo tiene todo local; `pilates_only` los de Pilates
 * (Solo Reformer); `strength` las salas de musculación (plan Musculación, RN-PLA-33).
 */
export const LOCATION_SCOPES = enumOf(["all", "pilates_only", "strength"]);
export const PRICE_LIST_STATUSES = enumOf(["draft", "active", "superseded"]);
/** RN-PLA-10: precio distinto por medio de pago y cantidad de cuotas. */
export const PRICE_MEDIUMS = enumOf(["cash", "card", "debit"]);
/**
 * RN-PLA-14 (flyers octubre 2026): los planes largos se pagan en 1 a 12 cuotas; cuántas
 * admite cada plan es su `maxInstallments`, y cuáles se venden de verdad lo dice la lista
 * vigente. Los planes no largos van en 1 pago.
 */
export const MAX_INSTALLMENTS = 12;
/** Las modalidades posibles en orden: 1, 2, …, 12. Para selectores; la regla es el rango. */
export const INSTALLMENT_OPTIONS = Array.from({ length: MAX_INSTALLMENTS }, (_, index) => index + 1);
/** ¿`n` es una cantidad de cuotas válida (entero entre 1 y `MAX_INSTALLMENTS`)? */
export function isValidInstallments(n) {
    return typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= MAX_INSTALLMENTS;
}
export const PROMOTION_TYPES = enumOf([
    "discount_pct",
    "fixed_amount",
    "special_price",
    "bonus_days",
]);
/** RN-SOC-22/23: sólo el dueño crea. */
export const BENEFIT_KINDS = enumOf(["courtesy_100", "permanent_discount", "barter"]);
export const BENEFIT_STATUSES = enumOf(["active", "revoked"]);
export const MEMBERSHIP_STATUSES = enumOf([
    "active",
    "scheduled",
    "frozen",
    "expired",
    "transferred",
    "canceled",
]);
/** RN-CAJ-01: una colección para todo lo que una persona debe. */
export const CHARGE_KINDS = enumOf([
    "membership_due",
    "plan_installment",
    "enrollment_fee",
    "class_extra",
    "event_fee",
    "trial_class",
    "agreement_installment",
]);
/** `in_collection` = débito con reintentos pendientes: el bloqueo espera (RN-PLA-25). */
export const CHARGE_STATUSES = enumOf([
    "pending",
    "in_collection",
    "paid",
    "overdue",
    "forgiven",
    "reversed",
]);
/** RN-PLA-18/19: vacaciones auto-aprobado; especial es excepción D5. */
export const FREEZE_KINDS = enumOf(["vacation", "special"]);
export const FREEZE_STATUSES = enumOf(["requested", "active", "finished", "rejected", "lifted"]);
/** RN-PLA-29: al día no bloquea; una cuota vencida reactiva bloqueo y avisos. */
export const PAYMENT_AGREEMENT_STATUSES = enumOf(["active", "completed", "defaulted"]);
export const CARD_BRANDS = enumOf(["visa", "mastercard", "other"]);
/**
 * Medios de Payway Pagos online con los que recepción adhiere al débito (RN-PLA-23): el
 * `payment_method_id` que pide `POST /payments` sale de la marca (detectada por el BIN) y
 * de si la tarjeta es de crédito o de débito. `cardBrand` es la marca que guarda la adhesión
 * (`CARD_BRANDS`); las que no son Visa ni Mastercard quedan como `other` y el nombre exacto
 * se lee de `label` con el `paymentMethodId` guardado.
 */
export const PAYWAY_CARD_METHODS = [
    { id: 1, network: "visa", kind: "credit", label: "Visa", cardBrand: "visa" },
    { id: 31, network: "visa", kind: "debit", label: "Visa Débito", cardBrand: "visa" },
    { id: 104, network: "mastercard", kind: "credit", label: "Mastercard", cardBrand: "mastercard" },
    {
        id: 105,
        network: "mastercard",
        kind: "debit",
        label: "Mastercard Débito",
        cardBrand: "mastercard",
    },
    { id: 106, network: "maestro", kind: "debit", label: "Maestro", cardBrand: "mastercard" },
    { id: 63, network: "cabal", kind: "credit", label: "Cabal", cardBrand: "other" },
    { id: 108, network: "cabal", kind: "debit", label: "Cabal Débito", cardBrand: "other" },
    { id: 65, network: "amex", kind: "credit", label: "American Express", cardBrand: "other" },
    { id: 24, network: "naranja", kind: "credit", label: "Naranja", cardBrand: "other" },
];
export const PAYWAY_CARD_METHOD_IDS = PAYWAY_CARD_METHODS.map((m) => m.id);
export function paywayCardMethodById(id) {
    return PAYWAY_CARD_METHODS.find((m) => m.id === id) ?? null;
}
/** RN-PLA-24/26. */
export const DEBIT_AUTHORIZATION_STATUSES = enumOf([
    "active",
    "scheduled_cancel",
    "canceled",
    "suspended",
]);
export const DEBIT_RUN_STATUSES = enumOf(["generated", "submitted", "reconciled"]);
export const DEBIT_ATTEMPT_RESULTS = enumOf(["approved", "rejected"]);
// ─────────────────────────────────────────────────────────────────────────────
// Caja y dinero (modelo-datos §2.5)
// ─────────────────────────────────────────────────────────────────────────────
/** RN-CAJ-02: los únicos medios de pago soportados. */
export const PAYMENT_METHODS = enumOf([
    "cash",
    "payway_terminal",
    "payway_recurring",
    "payway_online",
    "transfer",
    "mp_point",
    "mp_qr",
]);
/**
 * RN-CAJ-16/18: cómo cobra el mostrador con el posnet (`cash.posTerminalMode`).
 * `integrated` = el sistema le manda el importe a la terminal por API; `manual` = se pasa la
 * tarjeta en el posnet y recepción carga el cupón.
 */
export const POS_TERMINAL_MODES = enumOf(["integrated", "manual"]);
/** Tipo de tarjeta del cupón del posnet, cuando recepción lo anota. */
export const POS_CARD_TYPES = enumOf(["debit", "credit"]);
/** El convenio paga como `platform` (RN-CAJ-26). */
export const PAYER_KINDS = enumOf(["member", "visitor", "trial", "external", "platform"]);
/** RN-CAJ-19.3/19.5: transferencias nacen pendientes pero habilitan al socio. */
export const PAYMENT_STATUSES = enumOf([
    "confirmed",
    "pending_reconciliation",
    "observed",
    "reversed",
]);
/** RN-CAJ-22: un solo estado terminal de reversa; el POR QUÉ vive acá. */
export const REVERSAL_KINDS = enumOf(["void", "refund", "correction"]);
/**
 * RN-CAJ-19.3: quién declaró la transferencia. `mercadopago` NO la declara una persona:
 * la trae el sistema leyendo los movimientos de la cuenta de Mercado Pago del gimnasio
 * vinculada por OAuth. Por eso ninguna API pública la acepta como valor de entrada
 * (`transferInput` usa `TRANSFER_DECLARED_VIA_INPUT`).
 */
export const TRANSFER_DECLARED_VIA = enumOf(["reception", "app", "mercadopago"]);
/** Los orígenes que una persona puede declarar desde el mostrador o la app. */
export const TRANSFER_DECLARED_VIA_INPUT = enumOf(["reception", "app"]);
/** RN-CAJ-05/06: 2 turnos por día operativo; fines de semana y feriados, uno solo. */
export const CASH_SHIFT_SLOTS = enumOf(["morning", "afternoon", "single"]);
export const CASH_SHIFT_STATUSES = enumOf(["open", "closed", "reviewed"]);
/**
 * Unidad de negocio de una caja y de sus cobros. El kiosko es un negocio aparte del
 * gimnasio (Maxi, 15-oct-2026): su efectivo tiene su propio turno por sede, con apertura
 * y cierre, y nunca se mezcla con el de la recepción.
 */
export const BUSINESS_UNITS = enumOf(["gym", "kiosk"]);
/** Libro de caja INMUTABLE (RN-CAJ-08/15/22). */
export const CASH_MOVEMENT_KINDS = enumOf([
    "cash_in",
    "petty_expense",
    "envelope_out",
    "refund_out",
    "adjustment",
    /** Compra a un proveedor del kiosko pagada con el efectivo de la caja del kiosko. */
    "kiosk_purchase",
]);
/** RN-CAJ-07/12/17/21: toda diferencia con responsable y estado. */
export const CASH_DIFFERENCE_KINDS = enumOf([
    "opening",
    "closing",
    "audit",
    "pos_batch",
    "double_charge",
]);
export const CASH_DIFFERENCE_STATUSES = enumOf([
    "unjustified",
    "justified",
    "deducted",
    "assumed_by_house",
]);
/** RN-CAJ-13/14. */
export const ENVELOPE_STATUSES = enumOf(["in_branch", "withdrawn"]);
/** RN-CAJ-19.4: la conciliación es asistida, nunca silenciosa. */
export const BANK_FEED_MATCH_STATUSES = enumOf(["unmatched", "proposed", "matched", "ignored"]);
export const RECONCILIATION_PROPOSERS = enumOf(["system", "user"]);
export const RECONCILIATION_STATUSES = enumOf(["proposed", "confirmed", "rejected"]);
export const POS_BATCH_STATUSES = enumOf(["open", "reconciled", "with_differences"]);
// ─────────────────────────────────────────────────────────────────────────────
// Contable y facturación (modelo-datos §2.6)
// ─────────────────────────────────────────────────────────────────────────────
/** RN-CON-02: el tipo C queda soportado pero INACTIVO mientras el emisor sea RI (PV-75). */
export const INVOICE_TYPES = enumOf(["FA", "FB", "FC", "NCA", "NCB", "NCC", "NDA", "NDB", "NDC"]);
/** RN-CON-06: ARCA caído nunca bloquea el cobro. */
export const INVOICE_STATUSES = enumOf(["pending_cae", "issued", "failed"]);
/**
 * Por qué un comprobante quedó **pendiente** (RN-CON-06). No es un estado nuevo: el
 * comprobante sigue `pending_cae` y la cola lo emite cuando la causa se resuelve.
 * - `pos_not_enabled`: ARCA rechazó porque el punto de venta del emisor no está habilitado
 *   para facturar por webservice (RECE) — p. ej. sólo tiene CAEA. Lo resuelve el titular
 *   dando de alta el PV; la cola lo reintenta solo.
 * - `cae_queue`: ARCA no contestó (transitorio); la cola reintenta con backoff.
 * - `not_created`: la venta no llegó a tener comprobante (falta configuración); se factura
 *   a demanda desde Comprobantes.
 */
export const INVOICE_PENDING_CODES = enumOf(["pos_not_enabled", "cae_queue", "not_created"]);
/** Motivo visible del pendiente por PV no habilitado (lo que lee quien mira Comprobantes). */
export const INVOICE_PENDING_POS_NOT_ENABLED = "PV RECE no habilitado";
export const INVOICE_RECEIVER_KINDS = enumOf(["final_consumer", "identified"]);
export const INVOICE_RECEIVER_DOC_TYPES = enumOf(["dni", "cuit"]);
/** RN-CON-11/12. */
export const EXPENSE_METHODS = enumOf(["cash_register", "transfer", "other"]);
export const EXPENSE_STATUSES = enumOf(["pending", "approved", "rejected"]);
/** RN-CON-14: caja chica nace desactivada. */
export const PETTY_CASH_MOVEMENT_KINDS = enumOf(["expense", "replenishment"]);
/** RN-CON-15. */
export const SUPPLIER_PAYMENT_MODES = enumOf(["monthly", "on_stock"]);
/** RN-CON-19..22. */
export const PLATFORM_SETTLEMENT_STATUSES = enumOf(["generated", "sent", "reported", "collected"]);
// ─────────────────────────────────────────────────────────────────────────────
// Kiosko y stock (modelo-datos §2.7)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * RN-CON-24: no existe «transferir entre sedes».
 * `internal_consumption`: lo que el dueño o el staff retiran del kiosko a su nombre — baja
 * stock de la sede donde se retira, pero no es venta, ni cobro, ni egreso.
 */
export const STOCK_MOVEMENT_KINDS = enumOf([
    "purchase",
    "sale",
    "shrinkage",
    "adjustment",
    "internal_consumption",
]);
/** RN-CON-29: la diferencia NO ajusta nada sola. */
export const STOCK_COUNT_STATUSES = enumOf(["pending_adjustment", "adjusted", "dismissed"]);
export const SALE_STATUSES = enumOf(["completed", "voided"]);
/**
 * Por dónde entró una venta de kiosko: el mostrador atendido (portal o tablet con
 * operador) o la tablet PÚBLICA, donde el socio compra solo y paga con QR de Mercado Pago.
 */
export const SALE_CHANNELS = enumOf(["counter", "public_tablet"]);
// ─────────────────────────────────────────────────────────────────────────────
// Mercado Pago vinculado por OAuth
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Las dos cuentas de MP que el sistema puede vincular: la del gimnasio (donde los socios
 * transfieren la cuota) y la del kiosko (la que cobra con QR en la tablet pública).
 */
export const MP_ACCOUNT_PURPOSES = enumOf(["gym", "kiosk"]);
export const MP_ACCOUNT_STATUSES = enumOf(["linked", "unlinked", "error"]);
/**
 * Transferencia recibida en la cuenta del gimnasio: `pending` espera a una persona,
 * `auto` la concilió el sistema (un solo socio con ese DNI y el importe exacto de sus
 * cuotas), `manual` la concilió administración, `ignored` no es una cuota (con motivo).
 */
export const MP_TRANSFER_STATUSES = enumOf(["pending", "auto", "manual", "ignored"]);
/** Pedido de la tablet pública del kiosko, pagado con un QR dinámico de MP. */
export const KIOSK_ORDER_STATUSES = enumOf(["pending", "paid", "expired", "canceled", "failed"]);
// ─────────────────────────────────────────────────────────────────────────────
// Clases, grilla y reservas (modelo-datos §2.8)
// ─────────────────────────────────────────────────────────────────────────────
export const SCHEDULE_STATUSES = enumOf(["draft", "published"]);
/** RN-CLA-07: la baja notifica a fijos e inscriptos. */
export const SCHEDULED_CLASS_STATUSES = enumOf(["active", "removed"]);
/** RN-CLA-08, RN-PRO-17. */
export const CLASS_SESSION_STATUSES = enumOf([
    "scheduled",
    "canceled",
    "consolidated",
    "pending_confirm",
    "confirmed_no_students",
]);
/** Personas que ocupan cupo en una sesión. */
export const BOOKING_PERSON_KINDS = enumOf(["member", "visitor", "trial"]);
/** RN-CLA-15/21/32. */
export const BOOKING_ORIGINS = enumOf(["app", "reception", "fixed", "waitlist", "walkin"]);
/** RN-CLA-33/34. */
export const BOOKING_STATUSES = enumOf([
    "booked",
    "canceled_in_time",
    "canceled_late",
    "present",
    "absent",
]);
/** RN-CLA-30/31. */
export const CHECKIN_METHODS = enumOf(["room_qr", "teacher_manual", "coordinator_fix"]);
/** RN-CLA-20/22bis. */
export const FIXED_SLOT_STATUSES = enumOf(["requested", "active", "suspended", "dropped"]);
/** D2, RN-CLA-27..29. */
export const WAITLIST_STATUSES = enumOf([
    "waiting",
    "offered",
    "confirmed",
    "declined",
    "expired",
    "closed",
]);
/** RN-CLA-22/34. */
export const PENALTY_KINDS = enumOf(["booking_restriction", "fixed_slot_block"]);
/** RN-CLA-25: valen dentro de la misma semana, jamás pasan. */
export const RECOVERY_CREDIT_ORIGINS = enumOf([
    "canceled_in_time",
    "club_canceled",
    "holiday",
    "manual",
]);
export const RECOVERY_CREDIT_STATUSES = enumOf(["available", "used", "expired"]);
/**
 * `referralCredits.status` (RN-PLA-20, promo referidos): el crédito del socio que refirió
 * se usa una sola vez, en su próxima cuota, o vence. `expired` también cubre el crédito
 * cuyo cobro de origen se anuló (`voidedByPaymentId`).
 */
export const REFERRAL_CREDIT_STATUSES = enumOf(["available", "used", "expired"]);
/** `family.discountTarget` (RN-PLA-05 / RN-SOC-19): a qué cuota del grupo va el %. */
export const FAMILY_DISCOUNT_TARGETS = enumOf(["lowest_plan", "each_member"]);
/** RN-CLA-41/42. */
export const EVENT_KINDS = enumOf(["master", "viva_games"]);
export const EVENT_STATUSES = enumOf(["draft", "published", "closed", "canceled"]);
/**
 * `expired` = quedó sin pagar pasado `event.registrationExpiryHours` y liberó el cupo
 * (RN-CLA-42: la inscripción se confirma al pagar; impaga no retiene el lugar para siempre).
 */
export const EVENT_REGISTRATION_STATUSES = enumOf([
    "pending_payment",
    "confirmed",
    "refunded",
    "expired",
]);
// ─────────────────────────────────────────────────────────────────────────────
// Acceso físico y fichaje (modelo-datos §2.9)
// ─────────────────────────────────────────────────────────────────────────────
/** `accessLogs.person.kind` — `unknown` = identificación no resuelta (RN-ACC-15). */
export const ACCESS_PERSON_KINDS = enumOf(["member", "visitor", "trial", "staff", "unknown"]);
/** RN-ACC-04. */
export const ACCESS_METHODS = enumOf([
    "fingerprint",
    "member_qr",
    "location_qr",
    "manual",
    "agreement",
]);
/** RN-ACC-08: el semáforo. */
export const ACCESS_RESULTS = enumOf(["allowed", "allowed_warning", "rejected"]);
/** Motivos canónicos del rechazo/amarillo (RN-ACC-08..11, RN-ACC-19). */
export const ACCESS_REASONS = enumOf([
    "debt",
    "medical_certificate_expired",
    "medical_certificate_grace",
    "membership_frozen",
    "plan_without_access",
    "weekly_limit_reached",
    "agreement_not_valid",
    "qr_already_used",
    "qr_expired",
    "location_closed",
    "admission_ban",
    "antipassback",
    "unknown_person",
    /** Ficha dada de baja o archivada: no es «no identificada», es identificada y sin derecho. */
    "person_inactive",
    /** Sin plan vigente (RN-ACC-08.4: «deuda / plan vencido»). */
    "membership_expired",
]);
/** RN-ACC-21/23. */
export const TIMECLOCK_DIRECTIONS = enumOf(["in", "out"]);
export const TIMECLOCK_METHODS = enumOf(["fingerprint", "pin", "personal_qr", "mobile"]);
/** Arquitectura §5.1/§8: dispositivos dados de alta, con credencial revocable. */
export const DEVICE_KINDS = enumOf(["kiosk_tablet", "access_agent"]);
export const DEVICE_STATUSES = enumOf(["active", "revoked"]);
/**
 * Vinculación de una tablet por código (pedido de Damián, 21-oct-2026): la tablet muestra
 * un código de un solo uso, alguien con sesión lo aprueba y la api key viaja directo a la
 * tablet sin que ninguna persona la vea.
 */
export const DEVICE_PAIRING_STATUSES = enumOf([
    "pending",
    "approved",
    "claimed",
    "expired",
    "cancelled",
]);
// ─────────────────────────────────────────────────────────────────────────────
// Personal y liquidaciones (modelo-datos §2.10)
// ─────────────────────────────────────────────────────────────────────────────
export const WORK_SHIFT_KINDS = enumOf(["sala", "reception"]);
/**
 * RN-PRO-12/14/15: la vara sube al cierre o se ajusta a mano con motivo. `migration` = la
 * vara que fijó el pase a la regla del promedio por clase de la grilla (oct-2026).
 */
export const PERFORMANCE_BAR_SOURCES = enumOf(["auto_close", "manual", "migration"]);
export const PAYROLL_SETTLEMENT_STATUSES = enumOf(["draft", "in_review", "closed"]);
/** RN-PRO-13/27/28: el detalle clase por clase. */
export const PAYROLL_LINE_KINDS = enumOf([
    "class_hour",
    "sala_hour",
    "reception_hour",
    "premium",
    "trial_conversion",
    "commission",
    "retro_adjustment",
]);
/** RN-PRO-29: semáforo de la pre-liquidación. */
export const PAYROLL_PENDING_FLAGS = enumOf([
    "unconfirmed_classes",
    "timeclock_discrepancies",
    "external_invoice_missing",
]);
export const EXTERNAL_INVOICE_STATUSES = enumOf(["pending", "received"]);
/**
 * RN-PRO-08: discrepancias del cruce fichada × turno, más allá de la tolerancia.
 * Las cuatro primeras y `orphan` son las que nombra el AF; `no_clock` es la derivada de
 * RN-PRO-02 («ninguna hora se liquida sin origen registrado»): un turno planificado sin
 * NINGUNA fichada no se puede liquidar en silencio, tiene que verlo el coordinador.
 */
export const TIMECLOCK_DISCREPANCY_KINDS = enumOf([
    "late",
    "early_leave",
    "overtime",
    "outside_shift",
    "orphan",
    "no_clock",
]);
/** RN-PRO-08: cómo resolvió el coordinador la discrepancia, con motivo (D5). */
export const TIMECLOCK_RESOLUTIONS = enumOf(["approve", "reject", "adjust"]);
// ─────────────────────────────────────────────────────────────────────────────
// Comunicación y CRM (modelo-datos §2.11)
// ─────────────────────────────────────────────────────────────────────────────
/** RN-SOC-17/20: avisos del menor van al responsable; el pagador externo también recibe. */
export const NOTIFICATION_RECIPIENT_KINDS = enumOf([
    "member",
    "visitor",
    "user",
    "guardian",
    "external_payer",
]);
/** D3: los tres canales del sistema. */
export const NOTIFICATION_CHANNELS = enumOf(["push", "email", "inapp"]);
/**
 * **Nombres canónicos de los avisos** (RN-CRM-10/11). Un aviso tiene UN nombre y es el
 * mismo string en el catálogo (`settings.notifications.catalog`), en quien lo emite
 * (`notify({ type })`), en el centro de avisos y en la app. El dispatcher decide si un
 * aviso está apagado mirando `catalog[type]`: si el emisor y el catálogo no usan el mismo
 * string, apagarlo desde el portal no lo apaga. Por eso nadie escribe el string a mano:
 * se usa esta constante.
 *
 * Los 9 primeros son los del AF §10.4; los `wall_*` son los avisos sociales del muro
 * (mejora aprobada el 25-sep-2026); los dos últimos no están en el catálogo pero tienen
 * acción en la app (ver `NOTICE_ACTION_KINDS`).
 */
export const NOTICE_TYPES = {
    /** #1 Cuota por vencer. */
    dueSoon: "due_soon",
    /** #2 Deuda / cuota vencida (también el débito rechazado y el reenvío manual). */
    debt: "debt",
    /** #3 Cumpleaños. */
    birthday: "birthday",
    /** #4 Clase cancelada. */
    classCanceled: "class_canceled",
    /** #5 Lugar liberado: abre la ventana de confirmación de la lista de espera (D2). */
    spotAvailable: "spot_available",
    /** #6 Socio 2 semanas ausente. */
    absent14d: "absent_14d",
    /** #7 Clase de prueba agendada (al profe). */
    trialScheduled: "trial_scheduled",
    /** #8 Apto médico por vencer / vencido / gracia agotada. */
    aptExpiring: "apt_expiring",
    /** #9 Confirmación pendiente de lista de espera por vencer. */
    waitlistReminder: "waitlist_reminder",
    /** Muro: el moderador aprobó tu publicación. */
    wallApproved: "wall_approved",
    /** Muro: el moderador rechazó tu publicación (con motivo). */
    wallRejected: "wall_rejected",
    /** Muro: alguien comentó tu publicación. */
    wallComment: "wall_comment",
    /** Muro: reacciones a tu publicación, agrupadas. */
    wallReaction: "wall_reaction",
    /** Fuera del catálogo: la clase cambió de horario/salón/profe (RN-CLA-07). */
    classChanged: "class_changed",
    /** Fuera del catálogo: la sede respondió tu mensaje (RN-CRM-17). */
    messageReply: "message_reply",
    /** Fuera del catálogo: recibimos tu pedido de baja; sigue por Mensajes (RN-SOC-35). */
    terminationRequest: "termination_request",
};
/** Los 9 avisos del AF §10.4 + los 4 sociales: las claves del catálogo configurable. */
export const NOTICE_CATALOG_TYPES = enumOf([
    NOTICE_TYPES.dueSoon,
    NOTICE_TYPES.debt,
    NOTICE_TYPES.birthday,
    NOTICE_TYPES.classCanceled,
    NOTICE_TYPES.spotAvailable,
    NOTICE_TYPES.absent14d,
    NOTICE_TYPES.trialScheduled,
    NOTICE_TYPES.aptExpiring,
    NOTICE_TYPES.waitlistReminder,
    NOTICE_TYPES.wallApproved,
    NOTICE_TYPES.wallRejected,
    NOTICE_TYPES.wallComment,
    NOTICE_TYPES.wallReaction,
]);
/**
 * RN-CRM-11 / pantalla «Centro de avisos»: tocar un aviso lleva a la acción. Es lo que
 * viaja en `action.kind` del centro y del `data` del push.
 */
export const NOTICE_ACTION_KINDS = enumOf([
    "pay",
    "confirm_spot",
    "view_class",
    "upload_apt",
    "open_conversation",
    "open_post",
    "none",
]);
/** Claves del catálogo de avisos (RN-CRM-10) + alertas (RN-ROL-14, RN-REP-11/12). */
export const NOTIFICATION_TYPES = enumOf([
    ...NOTICE_CATALOG_TYPES,
    "security_alert",
    "business_alert",
    "cash_close",
    "daily_digest",
    /** AF §7.11 «Mis clases»: el profe avisa que falta y el coordinador busca reemplazo (D3). */
    "teacher_absence",
    /** RN-PRO-22: el profe bajó de N clases semanales y pierde personal training. */
    "personal_training_lost",
    /** RN-PRO-17: clase con 0 presentes esperando la marca «dictada sin alumnos». */
    "class_pending_confirm",
    /** RN-PRO-28/29: la pre-liquidación del mes ya está lista para revisar. */
    "payroll_ready",
]);
export const CAMPAIGN_STATUSES = enumOf(["draft", "scheduled", "sent", "canceled"]);
/** RN-CRM-07/18: el contacto humano se registra sobre socio o interesado. */
export const CONTACT_PERSON_KINDS = enumOf(["member", "prospect"]);
/** RN-CRM-16: la bandeja ordena por sin-responder más antiguo. */
export const CONVERSATION_STATUSES = enumOf(["unanswered", "answered", "closed"]);
export const MESSAGE_SENDER_KINDS = enumOf(["person", "staff", "system"]);
/**
 * Pedidos que el socio hace desde la app y caen en la bandeja de su sede como un mensaje
 * con datos estructurados. `termination` = pedido de baja (RN-SOC-35, AF §3 «Mi perfil»:
 * «pedido de baja → bandeja del portal admin»); la sede lo procesa con la baja del portal.
 */
export const MESSAGE_REQUEST_KINDS = enumOf(["termination"]);
/** RN-CRM-12/13: noticia del club o post del muro social. */
export const POST_KINDS = enumOf(["news", "social"]);
export const POST_AUTHOR_KINDS = enumOf(["staff", "member"]);
/**
 * Estados de `posts` (RN-CRM-12..14).
 * - Muro: `pending` (espera moderación previa) → `approved` | `rejected` (el moderador no
 *   la aprobó; sale de la cola) · `hidden` (oculta: por denuncia o por un moderador, ver
 *   `POST_HIDDEN_BY`) · `removed` (la eliminó un moderador) · `deleted` (la borró su
 *   autor). `removed` y `deleted` se purgan a los 90 días.
 * - Noticias: `draft` · `approved` (= publicada; visible desde `publishedAt`) ·
 *   `unpublished` (despublicada) · `removed` (borrada). La API de noticias habla en
 *   `NEWS_STATUSES`; `approved` es cómo se guarda «publicada» para que noticia y post
 *   compartan el filtro de «visible».
 */
export const POST_STATUSES = enumOf([
    "pending",
    "approved",
    "rejected",
    "hidden",
    "removed",
    "deleted",
    "draft",
    "unpublished",
]);
/** Quién ocultó: una denuncia (preventivo, RN-CRM-13) o un moderador (RN-CRM-14). */
export const POST_HIDDEN_BY = enumOf(["report", "moderator"]);
/** `removed` = lo eliminó un moderador; `deleted` = lo borró su autor. Ambos se purgan. */
export const POST_COMMENT_STATUSES = enumOf(["visible", "hidden", "removed", "deleted"]);
/** RN-CRM-13: motivo de una denuncia (enum corto + texto opcional). */
export const REPORT_REASONS = enumOf(["spam", "offensive", "inappropriate", "other"]);
/** Estado de una noticia tal como lo habla la API (RN-CRM-12). */
export const NEWS_STATUSES = enumOf(["draft", "published", "unpublished"]);
/** RN-CRM-14 sobre un comentario: restituir, ocultar o eliminar. */
export const COMMENT_MODERATION_ACTIONS = enumOf(["approve", "hide", "delete"]);
// ─────────────────────────────────────────────────────────────────────────────
// Sistema (modelo-datos §2.12)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * RN-ROL-10: superficie de cada mutación auditada. `system` son los jobs.
 */
export const SURFACES = enumOf([
    "admin_portal",
    "teachers_portal",
    "members_app",
    "kiosk",
    "access_point",
    "system",
]);
/** `settings.scope.level` — un parámetro es global o de una sede. */
export const SETTING_SCOPES = enumOf(["global", "branch"]);
export const MIGRATION_RUN_KINDS = enumOf(["test", "delta", "final"]);
export const MIGRATION_RUN_STATUSES = enumOf(["running", "completed", "failed"]);
export const MIGRATION_VALIDATION_STATUSES = enumOf(["ok", "error"]);
/**
 * `training` se suma a los tres del modelo de datos porque RN-MIG-13/14 son ítems del
 * MISMO checklist (capacitación por rol y manual corto) y el corte no se habilita sin
 * ellos (RN-MIG-16): si no fueran del checklist, serían una promesa sin estado.
 */
export const LAUNCH_CHECKLIST_KINDS = enumOf([
    "initial_load",
    "validation",
    "module_approval",
    "training",
    /**
     * RN-MIG-10: bajas del mundo viejo (licencia de Crossfy, archivo de Control Gym,
     * revocación de accesos). Son POSTERIORES al corte por definición, así que no lo
     * gatean — pero si no estuvieran en el checklist, nadie se acordaría de apagarlas.
     */
    "decommission",
]);
export const LAUNCH_CHECKLIST_STATUSES = enumOf(["pending", "loaded", "verified", "approved"]);
// ─────────────────────────────────────────────────────────────────────────────
// API (contracts.md §0)
// ─────────────────────────────────────────────────────────────────────────────
/** Códigos de error del contrato §0. */
export const ERROR_CODES = enumOf([
    "VALIDATION",
    "UNAUTHORIZED",
    "FORBIDDEN",
    "NOT_FOUND",
    "CONFLICT",
    "RULE_VIOLATION",
    "RATE_LIMITED",
    "INTERNAL",
    /**
     * 503: el cobro online no está disponible (checkout de Payway sin cargar o todavía sin
     * homologar). La app lo muestra tal cual y ofrece pagar en recepción (RN-PLA-31).
     */
    "PAYMENTS_UNAVAILABLE",
    /** 410: existió pero ya no sirve (un código de vinculación de tablet vencido). */
    "EXPIRED",
    /**
     * 409: el precio de venta de un producto del kiosko queda por debajo de su último costo.
     * Se guarda igual sólo con la confirmación explícita y un motivo (queda auditado).
     */
    "PRICE_BELOW_COST",
]);
/** Catálogos administrables (RN-CON-11, RN-CRM-02/03, RN-PLA-20). */
export const CATALOG_KINDS = enumOf([
    "expense_categories",
    "contact_sources",
    "lost_reasons",
    "promotion_tags",
    "termination_reasons",
    "contact_channels",
]);
// ─────────────────────────────────────────────────────────────────────────────
// CRM: segmentos y catálogos (AF §10, PV-84)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * RN-CRM-08: los segmentos para campañas de retención. Los seis del AF (PV-84).
 * `lost_by_reason` se parametriza con el motivo del catálogo de RN-CRM-03.
 */
export const RETENTION_SEGMENTS = enumOf([
    "absent_14d",
    "overdue",
    "plan_expiring",
    "birthday_month",
    "former_members",
    "lost_prospects",
]);
/** RN-CRM-14: qué hizo el moderador con un contenido del muro. */
export const MODERATION_ACTIONS = enumOf(["approve", "reject", "hide", "remove", "restore"]);
/** RN-CRM-18: de dónde sale cada línea de la línea de tiempo de comunicaciones. */
export const COMMUNICATION_SOURCES = enumOf([
    "notification",
    "campaign",
    "conversation",
    "contact_log",
    "task",
]);
//# sourceMappingURL=enums.js.map